import { WebGLRenderer } from 'three';
import { create, Mutate, StateCreator, StoreApi, StoreMutatorIdentifier } from 'zustand';
import { combine } from 'zustand/middleware';

import { advance, invalidate, RootState, three } from '@react-three/fiber';
import { World } from '@spazious/dollhouse-three';

type Write<T extends object, U extends object> = Omit<T, keyof U> & U;

type Cast<T, U> = T extends U ? T : U;

declare module 'zustand' {
  // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars, @typescript-eslint/no-unused-vars
  interface StoreMutators<S, A> {
    'spz/dollhouse': Write<
      Cast<S, object>,
      {
        dollhouse: {
          version: number;
        };
      }
    >;
  }
}

export interface DollhouseRootState extends Omit<RootState, 'gl'> {
  world: World;
  gl?: WebGLRenderer;
}

// https://docs.pmnd.rs/zustand/guides/typescript#middleware-that-changes-the-store-type

type DollhouseStateCreator = <
  T,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = []
>(
  f: StateCreator<T, [...Mps, ['spz/dollhouse', unknown]], Mcs>
) => StateCreator<OverwriteState<T, DollhouseRootState>, Mps, [['spz/dollhouse', unknown], ...Mcs]>;

type OverwriteState<T, U> = Omit<T, keyof U> & U;

type DollhouseStateCreatorImpl = <T>(
  f: StateCreator<T, [], [], DollhouseRootState>
) => StateCreator<OverwriteState<T, DollhouseRootState>, [], []>;

const dollhouseImpl: DollhouseStateCreatorImpl = (f) => (set, get, api) => {
  type T = ReturnType<typeof f>;

  const store = api as unknown as Mutate<StoreApi<T>, [['spz/dollhouse', unknown]]>;

  store.dollhouse = {
    version: 1,
  };

  const fn = combine(
    () => ({}),
    // @ts-ignore
    three(invalidate, advance)
  );
  // @ts-ignore
  return combine(fn(set, get, store), f)(set, get, store) as any;
};

export const dollhouse = dollhouseImpl as unknown as DollhouseStateCreator;

export const createDollhouseStore = <T, Mos extends [StoreMutatorIdentifier, unknown][] = []>(
  initializer: StateCreator<T, [], Mos> = (_, get) => get() as T
) => create(dollhouse(initializer));

export const store = createDollhouseStore(() => ({}));

// export const createDollhouseStore = (scene: Scene, world: World) =>
//   create(dollhouse(() => ({ scene, world })));

// export const store = createDollhouseStore(new Scene(), new World(new Scene()));

export type DollhouseStore = typeof store;
